import React, { useState, useRef, useEffect } from 'react';
import useBoardStore from '../store/boardStore';
import './Sticker.css';

const STICKER_COLORS = [
  '#fff3cd', // Default yellow
  '#d1ecf1', // Light blue
  '#d4edda', // Light green
  '#f8d7da', // Light red
  '#e2e3e5', // Light gray
  '#fff3e0', // Light orange
  '#e7e3ff', // Light purple
  '#ffe6f0'  // Light pink
];

const Sticker = ({ sticker, onStickerClick }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [text, setText] = useState(sticker.text);
  
  const stickerRef = useRef(null);
  const textareaRef = useRef(null);
  
  const { updateSticker, isConnecting, setConnecting } = useBoardStore();
  
  useEffect(() => {
    setText(sticker.text);
  }, [sticker.text]);
  
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);
  
  const handleMouseDown = (event) => {
    if (isEditing) return;
    
    // Don't start dragging if we're connecting
    if (isConnecting) return;
    
    event.stopPropagation();
    setIsDragging(true);
    
    const rect = stickerRef.current.getBoundingClientRect();
    setDragOffset({
      x: event.clientX - rect.left - rect.width / 2,
      y: event.clientY - rect.top - rect.height / 2
    });
  };
  
  const handleMouseMove = (event) => {
    if (!isDragging) return;
    
    const svg = stickerRef.current.closest('svg');
    const svgRect = svg.getBoundingClientRect();
    
    const newX = event.clientX - svgRect.left - dragOffset.x;
    const newY = event.clientY - svgRect.top - dragOffset.y;
    
    updateSticker(sticker.id, {
      position: { x: newX, y: newY }
    });
  };
  
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);
  
  const handleClick = (event) => {
    event.stopPropagation();
    
    if (isConnecting) {
      console.log('Clicking on target sticker:', sticker.id, 'from:', isConnecting);
      onStickerClick(sticker.id);
      return;
    }
  };
  
  const handleDoubleClick = (event) => {
    event.stopPropagation();
    if (!isConnecting) {
      setIsEditing(true);
    }
  };
  
  const handleTextChange = (event) => {
    setText(event.target.value);
  };
  
  const handleTextBlur = () => {
    setIsEditing(false);
    updateSticker(sticker.id, { text });
  };
  
  const handleTextKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleTextBlur();
    }
    if (event.key === 'Escape') {
      setText(sticker.text);
      setIsEditing(false);
    }
  };
  
  const handleConnectorStart = (event) => {
    event.stopPropagation();
    console.log('Starting connector from sticker:', sticker.id);
    setConnecting(true, sticker.id);
  };

  const handleColorChange = (color) => {
    updateSticker(sticker.id, { color });
    setShowColorPicker(false);
  };

  const handleResizeStart = (event) => {
    event.stopPropagation();
    setIsResizing(true);
    
    const startX = event.clientX;
    const startY = event.clientY;
    const startWidth = sticker.size?.width || 120;
    const startHeight = sticker.size?.height || 80;
    
    const handleResize = (e) => {
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;
      const newWidth = Math.max(80, startWidth + deltaX);
      const newHeight = Math.max(60, startHeight + deltaY);
      
      updateSticker(sticker.id, {
        size: { width: newWidth, height: newHeight }
      });
    };
    
    const handleResizeEnd = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleResize);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
    
    document.addEventListener('mousemove', handleResize);
    document.addEventListener('mouseup', handleResizeEnd);
  };
  
  const stickerWidth = sticker.size?.width || 120;
  const stickerHeight = sticker.size?.height || 80;
  const stickerColor = sticker.color || '#fff3cd';
  const halfWidth = stickerWidth / 2;
  const halfHeight = stickerHeight / 2;

  return (
    <g
      ref={stickerRef}
      className={`sticker ${isDragging ? 'dragging' : ''} ${isConnecting ? 'connecting' : ''} ${isResizing ? 'resizing' : ''}`}
      transform={`translate(${sticker.position.x}, ${sticker.position.y})`}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      {/* Sticker background */}
      <rect
        x={-halfWidth}
        y={-halfHeight}
        width={stickerWidth}
        height={stickerHeight}
        rx="8"
        ry="8"
        fill={stickerColor}
        stroke={stickerColor === '#fff3cd' ? '#f0ad4e' : '#999'}
        strokeWidth="2"
        className="sticker-background"
      />
      
      {/* Connector handle */}
      <circle
        cx={halfWidth}
        cy="0"
        r="8"
        fill="#007bff"
        stroke="#fff"
        strokeWidth="2"
        className="connector-handle"
        onClick={handleConnectorStart}
      />
      
      {/* Color picker button */}
      <circle
        cx={-halfWidth + 15}
        cy={-halfHeight + 15}
        r="8"
        fill={stickerColor}
        stroke="#333"
        strokeWidth="2"
        className="color-picker-handle"
        onClick={(e) => {
          e.stopPropagation();
          setShowColorPicker(!showColorPicker);
        }}
      />
      
      {/* Resize handle */}
      <rect
        x={halfWidth - 8}
        y={halfHeight - 8}
        width="8"
        height="8"
        fill="#666"
        className="resize-handle"
        onMouseDown={handleResizeStart}
      />
      
      {/* Text content */}
      {isEditing ? (
        <foreignObject x={-halfWidth + 10} y={-halfHeight + 10} width={stickerWidth - 20} height={stickerHeight - 20}>
          <textarea
            ref={textareaRef}
            className="sticker-textarea"
            value={text}
            onChange={handleTextChange}
            onBlur={handleTextBlur}
            onKeyDown={handleTextKeyDown}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              resize: 'none',
              fontSize: '12px',
              fontFamily: 'inherit',
              padding: '4px'
            }}
          />
        </foreignObject>
      ) : (
        <text
          x="0"
          y="0"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="12"
          fill="#333"
          className="sticker-text"
        >
          {text.split('\n').map((line, index) => (
            <tspan key={index} x="0" dy={index === 0 ? 0 : '1.2em'}>
              {line}
            </tspan>
          ))}
        </text>
      )}
      
      {/* Color picker palette */}
      {showColorPicker && (
        <g className="color-picker-palette">
          <rect
            x={-halfWidth - 20}
            y={-halfHeight - 60}
            width="200"
            height="50"
            rx="5"
            fill="white"
            stroke="#ccc"
            strokeWidth="1"
          />
          {STICKER_COLORS.map((color, index) => (
            <circle
              key={color}
              cx={-halfWidth - 10 + (index * 22)}
              cy={-halfHeight - 35}
              r="8"
              fill={color}
              stroke={color === stickerColor ? '#333' : '#999'}
              strokeWidth={color === stickerColor ? '3' : '1'}
              className="color-option"
              onClick={(e) => {
                e.stopPropagation();
                handleColorChange(color);
              }}
            />
          ))}
        </g>
      )}
    </g>
  );
};

export default Sticker;
